import { createContext, useContext, useEffect, useState } from "react";
import { AuthContextType, Role } from "../../types/authcontext.types";
import { toast } from "react-toastify";
import { db } from "../../firebase";
import { set, get, ref } from "firebase/database";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

// Turn a Firebase auth error into something showable. The old inline regex
// (`error.message.match(...)[1]`) THREW on any message it didn't recognise —
// e.g. "popup closed by user" — crashing the sign-in flow inside its own
// error handler.
const authErrorMessage = (error: any): string => {
  const code: string = error?.code || error?.message?.match(/\(auth\/([^)]+)\)/)?.[1] || '';
  if (code.includes('popup-closed') || code.includes('cancelled')) return 'Sign-in cancelled';
  const cleaned = code.replace(/^auth\//, '').replace(/-/g, ' ').trim();
  return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : 'Sign-in failed — try again';
};

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  // Initialize state with properly parsed localStorage data
  const [token, setToken] = useState(() => localStorage.getItem("token") || null);
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem("user");
    return savedUser ? savedUser : null;
  });
  const [auth, setAuth] = useState<any>(null);
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    photoURL: '',
    role: '' as Role,
    createdAt: new Date().toISOString(),
    lastLogin: new Date().toISOString()
  });

  // Effect to update localStorage when token or user changes
  useEffect(() => {
    if (token) {
      localStorage.setItem("token", token);
    } else {
      localStorage.removeItem("token");
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      localStorage.setItem("user", user);
    } else {
      localStorage.removeItem("user");
    }
  }, [user]);

  // Firebase persists the auth session in browser storage. Restore it on reload
  // so `auth.currentUser` (used for answer writes / uid) survives a refresh —
  // previously `auth` was only set during the sign-in popup, so a player who
  // refreshed mid-quiz could no longer submit answers.
  useEffect(() => {
    const firebaseAuth = getAuth();
    return onAuthStateChanged(firebaseAuth, async (firebaseUser) => {
      if (firebaseUser) {
        setAuth(firebaseAuth);
        setUser(firebaseUser.uid);
        setToken((firebaseUser as any).accessToken ?? (await firebaseUser.getIdToken()));
      } else {
        setAuth(null);
        setUser(null);
        setToken(null);
      }
    });
  }, []);

  // Effect to fetch user info when token and user are available
  useEffect(() => {
    if (token && user) {
      (async () => {
        const userRef = ref(db, `users/${user}`);
        const userSnapshot = await get(userRef);
        if (userSnapshot.exists()) {
          setUserInfo(userSnapshot.val());
        }
      })();
    }
  }, [token, user]);

  // Google is the only sign-in method (SignIn.tsx). Email/password auth left
  // with the legacy SignUp page.
  const loginViaGoogle = async (authObj: any) => {
    try {
      const { currentUser } = authObj;
      const { uid, displayName, email, photoURL } = currentUser;

      setToken(currentUser.accessToken);
      setUser(uid);
      setAuth(authObj);

      const userRef = ref(db, `users/${uid}`);
      const userSnapshot = await get(userRef);
      
      if (!userSnapshot.exists()) {
        let userObj = {
          name: displayName,
          email: email,
          photoURL: photoURL,
          role: 'audience' as Role,
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString()
        }
        await set(userRef, userObj);
        setUserInfo(userObj);
      } else {
        await set(ref(db, `users/${uid}/lastLogin`), new Date().toISOString());
        setUserInfo(userSnapshot.val());
      }

      toast.success(`Logged In Successfully!`);
    } catch (error: any) {
      console.log(error.message);
      toast.error(authErrorMessage(error));
    }
  };

  const logout = () => {
    signOut(getAuth()); // clear the persisted Firebase session, not just local state
    setToken(null);
    setUser(null);
    setAuth(null);
    setUserInfo({
      name: '',
      email: '',
      photoURL: '',
      role: '' as Role,
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString()
    });
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    toast.success("Logged out successfully!");
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        setToken,
        user,
        setUser,
        userInfo,
        setUserInfo,
        loginViaGoogle,
        auth,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);
export { useAuth, AuthProvider };
